import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { fetchResourceById, fetchUserCollections } from '../../lib/api';
import {
  appSelectors,
  setLoggedOut,
  setUserCollectionLoading,
  setUserCollections,
} from '../../redux/appSlice';
import Breadcrumbs from '../BreadCrumbs';
import Modal from '../Modal';
import ResourceDetailsContent from '../ResourceDetails';
import SearchBar from '../SearchBar';
import SelectionBar from '../SelectionBar';
import CollectionSelect from '../CollectionSelect';

interface LayoutWrapperProps {
  children: ReactNode;
}

export const LayoutWrapper = ({ children }: LayoutWrapperProps) => {
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [dialogAddToCollectionOpen, setDialogAddToCollectionOpen] =
    useState<boolean>(false);
  const loggedIn: boolean = useAppSelector(appSelectors.isLoggedIn);
  const userCollectionsStale: boolean = useAppSelector(
    appSelectors.userCollectionsStale
  );

  const resourceId: number | undefined = useMemo<number | undefined>(() => {
    const param = searchParams.get('resource');
    return param ? parseInt(param) : undefined;
  }, [searchParams]);

  const addToCollection: boolean | undefined = useMemo<
    boolean | undefined
  >(() => {
    const param = searchParams.get('add');
    return param ? Boolean(param) : undefined;
  }, [searchParams]);

  useEffect(() => {
    if (resourceId !== null && dialogOpen === false && !addToCollection) {
      setDialogOpen(true);
      setDialogAddToCollectionOpen(false);
    }

    if (
      resourceId !== null &&
      dialogAddToCollectionOpen === false &&
      addToCollection
    ) {
      setDialogOpen(false);
      setDialogAddToCollectionOpen(true);
    }
  }, [resourceId, addToCollection, dialogOpen, dialogAddToCollectionOpen]);

  const handleModalClose = () => {
    setSearchParams((currentSearchParams: URLSearchParams): URLSearchParams => {
      currentSearchParams.delete('resource');
      return currentSearchParams;
    });
    setDialogOpen(false);
  };

  const handleAddToCollectionClose = () => {
    setSearchParams((currentSearchParams: URLSearchParams): URLSearchParams => {
      currentSearchParams.delete('add');
      return currentSearchParams;
    });
    setDialogAddToCollectionOpen(false);
    setDialogOpen(true);
  };

  const handleFetchError = useCallback(() => {
    if (loggedIn) {
      dispatch(setLoggedOut());
    }
  }, [loggedIn, dispatch]);

  const { isLoading: isResourceLoading, data: resourceData } = useQuery({
    queryKey: ['resource', resourceId],
    queryFn: () => (resourceId ? fetchResourceById(resourceId) : undefined),
    enabled: !!resourceId,
  });

  const {
    isLoading: userCollectionsLoading,
    data: userCollections,
    refetch,
  } = useQuery({
    queryKey: ['user_collections'],
    queryFn: fetchUserCollections,
    enabled: loggedIn,
    onError: handleFetchError,
  });

  useEffect(() => {
    if (userCollectionsStale && refetch) {
      refetch();
    }
  }, [userCollectionsStale, refetch]);

  useEffect(() => {
    dispatch(setUserCollectionLoading(userCollectionsLoading));
  }, [userCollectionsLoading, dispatch]);

  useEffect(() => {
    if (userCollections) dispatch(setUserCollections(userCollections));
  }, [userCollections, dispatch]);

  return (
    <>
      {resourceData && (
        <>
          <Modal
            showBack={true}
            onClose={handleModalClose}
            isOpened={dialogOpen}
            title={resourceData.metadata.title?.value}
          >
            <ResourceDetailsContent
              resourceData={resourceData}
              isLoading={isResourceLoading}
              resourceId={resourceId}
            />
          </Modal>
          <Modal
            showBack={true}
            title="Add your image to a collection"
            isOpened={dialogAddToCollectionOpen}
            onClose={handleAddToCollectionClose}
          >
            <CollectionSelect
              singleResourceId={resourceId}
              close={() => handleAddToCollectionClose()}
            />
          </Modal>
        </>
      )}
      <div className="header-wrapper">
        <Breadcrumbs />
        <SearchBar showBack={location.pathname !== '/'} />
      </div>
      {children}
      <SelectionBar />
    </>
  );
};
